import { useState } from "react";
import { Bell, BellRing, AlertTriangle, Loader2 } from "lucide-react";
import { Switch } from "./ui/switch";
import { Button } from "./ui/button";
import useWebPush from "../hooks/useWebPush";
import { toast } from "sonner";

/* PushNotificationsToggle
 * -----------------------
 * Drop-in card for the Security page. Lets a signed-in user enable or
 * disable Web Push notifications on the current device, and send a test
 * notification to confirm the wiring.
 */

export default function PushNotificationsToggle() {
  const {
    supported,
    iosNeedsInstall,
    permission,
    enabled,
    loading,
    enable,
    disable,
    test,
  } = useWebPush();
  const [testing, setTesting] = useState(false);

  const handleToggle = async (next) => {
    try {
      if (next) {
        await enable();
        toast.success("Notificaciones activadas en este dispositivo");
      } else {
        await disable();
        toast.success("Notificaciones desactivadas");
      }
    } catch (e) {
      toast.error(e.message || "No pudimos cambiar el estado");
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const res = await test();
      if ((res.delivered || 0) > 0) {
        toast.success(`Enviamos una notificación de prueba (${res.delivered}).`);
      } else {
        toast.warning("No hay dispositivos activos para esta cuenta.");
      }
    } catch (_e) {
      toast.error("No pudimos enviar la prueba.");
    } finally {
      setTesting(false);
    }
  };

  if (!supported) {
    return (
      <div className="card-base mb-6" data-testid="security-push-card">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-neutral-100 flex items-center justify-center shrink-0">
            <Bell className="w-5 h-5 text-neutral-500" />
          </div>
          <div>
            <h3 className="font-heading font-semibold text-lg text-agave">
              Notificaciones push
            </h3>
            <p className="text-sm text-neutral-600 mt-1">
              Tu navegador no soporta notificaciones push. Usa Chrome, Edge,
              Firefox o Safari actualizado.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const denied = permission === "denied";

  return (
    <div className="card-base mb-6" data-testid="security-push-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <div
            className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
              enabled ? "bg-terracotta/15 text-terracotta" : "bg-neutral-100 text-neutral-500"
            }`}
          >
            {enabled ? <BellRing className="w-5 h-5" /> : <Bell className="w-5 h-5" />}
          </div>
          <div className="min-w-0">
            <h3 className="font-heading font-semibold text-lg text-agave">
              Notificaciones push
            </h3>
            <p className="text-sm text-neutral-600 mt-1 max-w-[42ch]">
              Recibe confirmaciones de reservas, mensajes y recordatorios de
              viaje directo en este dispositivo, aún sin la app abierta.
            </p>
          </div>
        </div>

        <Switch
          checked={enabled}
          onCheckedChange={handleToggle}
          disabled={loading || denied || iosNeedsInstall}
          data-testid="security-push-toggle"
        />
      </div>

      {iosNeedsInstall && (
        <div
          className="mt-4 flex items-start gap-2 text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-xl p-3"
          data-testid="security-push-ios-hint"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <p>
            En iPhone primero agrega Rivones a tu pantalla de inicio (toca
            Compartir → <strong>Agregar a inicio</strong>) y abre la app desde
            el ícono. Después regresa aquí para activar las notificaciones.
          </p>
        </div>
      )}

      {denied && !iosNeedsInstall && (
        <div
          className="mt-4 flex items-start gap-2 text-sm text-red-900 bg-red-50 border border-red-200 rounded-xl p-3"
          data-testid="security-push-denied-hint"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <p>
            Bloqueaste las notificaciones para Rivones. Actívalas desde la
            configuración del sitio en tu navegador y vuelve a intentar.
          </p>
        </div>
      )}

      {enabled && (
        <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
          <p className="text-xs text-neutral-500">
            Activas en este dispositivo. Puedes activar más dispositivos
            iniciando sesión en cada uno.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={handleTest}
            disabled={testing}
            data-testid="security-push-test-btn"
          >
            {testing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <BellRing className="w-4 h-4 mr-2" />
            )}
            Enviar prueba
          </Button>
        </div>
      )}
    </div>
  );
}
